"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";
import i18n from "@/lib/i18n";
import {
  type RemoteThreadDetails,
} from "@/components/custom/providers/customThreadListProvider";
import type { ThreadItem } from "@/components/custom/contexts/threadContext";

export type UseThreadsListQueryArgs = {
  q: string;
  searchInContent: boolean;
  pageSize: number;
  caseId?: string;
};

export type UseThreadsListQueryResult = {
  items: ThreadItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => void;
  refetch: () => void;
};

/**
 * Server-paged thread list for the chats page. Drives off the same
 * `GET /api/threads` endpoint as the AUI sidebar's adapter, but with the
 * filter/take/skip parameters wired up. Pages are appended via `loadMore`
 * so already-loaded rows aren't re-traversed; arg changes (q, content
 * toggle, case) reset to skip=0 and refetch.
 *
 * Sidebar parity: the adapter calls `list()` without these params, hits the
 * same endpoint, gets the unfiltered ordered list — there's no contention.
 */
export function useThreadsListQuery({
  q,
  searchInContent,
  pageSize,
  caseId,
}: UseThreadsListQueryArgs): UseThreadsListQueryResult {
  const argsKey = useMemo(() => {
    return JSON.stringify({
      q: q.trim(),
      searchInContent,
      caseId: caseId ?? "",
    });
  }, [q, searchInContent, caseId]);

  const [pages, setPages] = useState<ThreadItem[][]>([]);
  // Cold-load state — true only when there's no data to display yet. Refetches
  // triggered by arg changes (typing, chip toggle, checkbox) leave isLoading
  // false so the previous items stay rendered and the page doesn't blink.
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs that stay in sync with the latest args so the abort/refetch closures
  // don't need to be re-created on every keystroke.
  const argsKeyRef = useRef(argsKey);
  const abortRef = useRef<AbortController | null>(null);
  // Tracks whether we've ever rendered items for the current session — flips
  // to true after the first successful fetch and never back. Lets us choose
  // "stale-while-revalidate" (keep old items) vs "cold load" (show skeleton).
  const hasLoadedOnceRef = useRef(false);

  const buildUrl = useCallback(
    (skip: number, take: number) => {
      const params = new URLSearchParams();
      const trimmed = q.trim();
      if (trimmed) params.set("q", trimmed);
      if (searchInContent) params.set("searchInContent", "true");
      params.set("excludeArchived", "true");
      params.set("take", String(take));
      if (skip > 0) params.set("skip", String(skip));
      if (caseId) params.set("caseId", caseId);
      return `api/threads?${params.toString()}`;
    },
    [q, searchInContent, caseId],
  );

  const mapResponse = (raw: RemoteThreadDetails[]): ThreadItem[] =>
    raw.map(thread => ({
      remoteId: thread.id,
      selectedDocs: thread.selectedDocs ?? [],
      isPinned: thread.is_pinned ?? false,
      createdAt: thread.created_at,
      updatedAt: thread.updated_at,
      archivedAt: thread.archived_at ?? null,
      title: thread.title,
      caseId: thread.case_id ?? null,
    }));

  // Field-level equality for two items at the same remoteId. Excludes
  // selectedDocs (array reference noise — chats page never reads it).
  const isSameItem = (a: ThreadItem, b: ThreadItem) =>
    a.remoteId === b.remoteId &&
    a.title === b.title &&
    (a.isPinned ?? false) === (b.isPinned ?? false) &&
    a.createdAt === b.createdAt &&
    a.updatedAt === b.updatedAt &&
    (a.archivedAt ?? null) === (b.archivedAt ?? null) &&
    (a.caseId ?? null) === (b.caseId ?? null);

  // Reuse prior references for rows whose content didn't change so React.memo
  // on ThreadRow can skip re-rendering them. When the entire page is identical,
  // returns the prev page reference verbatim — the setPages call on top of
  // this becomes a no-op (same reference) and the consumer doesn't re-render.
  const stabilizePage = (prevFlat: ThreadItem[], next: ThreadItem[]): ThreadItem[] => {
    if (prevFlat.length === 0) return next;
    const prevById = new Map(prevFlat.map(i => [i.remoteId, i]));
    let changed = false;
    const merged = next.map(item => {
      const old = prevById.get(item.remoteId);
      if (old && isSameItem(old, item)) return old;
      changed = true;
      return item;
    });
    return changed || merged.length !== prevFlat.length ? merged : prevFlat;
  };

  // Single fetch primitive used by the args-change effect, loadMore, and refetch.
  const fetchPage = useCallback(
    async (skip: number, append: boolean, expectedKey: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (append) {
        setIsLoadingMore(true);
      } else if (!hasLoadedOnceRef.current) {
        // First-ever load: show the cold-load affordance. Subsequent refetches
        // triggered by arg changes leave isLoading false so the prior items
        // stay rendered (stale-while-revalidate, no blink).
        setIsLoading(true);
      }
      setError(null);

      try {
        const res = await apiAuthFetch(buildUrl(skip, pageSize), {
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(i18n.t("error.code.service_unavailable"));
        }
        const raw: RemoteThreadDetails[] = await res.json();
        // Stale guard: if args changed mid-flight, drop the result silently —
        // a fresh request is already in flight from the args-change effect.
        if (argsKeyRef.current !== expectedKey) return;

        const mapped = mapResponse(raw);
        // Atomic swap on success — replace (or append) in a single setState so
        // the UI never flashes through an empty intermediate state. The
        // stabilize step reuses prior row references for unchanged content so
        // React.memo'd ThreadRows skip re-render when only the query keystrokes
        // around them change.
        setPages(prev => {
          if (append) return [...prev, mapped];
          const stabilized = stabilizePage(prev.flat(), mapped);
          // If structurally identical AND the wrapping page array would
          // collapse to one identical page, return prev verbatim — same
          // reference, no consumer re-render.
          if (prev.length === 1 && stabilized === prev[0]) return prev;
          return [stabilized];
        });
        setHasMore(mapped.length >= pageSize);
        hasLoadedOnceRef.current = true;
      } catch (err) {
        if (controller.signal.aborted) return; // cancelled by next call — not a user-facing error
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        ClientLogger.error("useThreadsListQuery fetch failed", {
          error: e,
          component: "useThreadsListQuery",
          action: `GET /threads (skip=${skip})`,
        });
      } finally {
        if (!controller.signal.aborted) {
          if (append) setIsLoadingMore(false);
          else setIsLoading(false);
        }
      }
    },
    [buildUrl, pageSize],
  );

  // Refetch on args change. We intentionally do NOT clear `pages` here —
  // keeping the previous items rendered while the new fetch is in flight is
  // what kills the blink on every keystroke / chip toggle / checkbox tick.
  // The atomic replace inside fetchPage on success swaps stale → fresh in one
  // setState; on error we keep the stale data and surface the error.
  useEffect(() => {
    argsKeyRef.current = argsKey;
    void fetchPage(0, false, argsKey);
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argsKey]);

  const items = useMemo(() => pages.flat(), [pages]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    void fetchPage(items.length, true, argsKeyRef.current);
  }, [isLoading, isLoadingMore, hasMore, items.length, fetchPage]);

  const refetch = useCallback(() => {
    // Same stale-while-revalidate behaviour as the args-change effect: keep
    // the current pages on screen until the fresh fetch lands, then atomically
    // swap. After-mutation refetches (rename, archive) feel instant this way
    // — the row updates in place rather than the whole list flashing.
    void fetchPage(0, false, argsKeyRef.current);
  }, [fetchPage]);

  return { items, isLoading, isLoadingMore, hasMore, error, loadMore, refetch };
}
